import { Link } from "react-router-dom";
import { useEffect } from "react";
import React, { useState } from "react";
import {
  ArrowRight,
  Lightbulb,
  Sparkles,
  Users,
  ShieldCheck,
  Target,
  Layers,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/About1.css/About.css";
import team from "../assets/ceo5.jpeg";
import aboutHeroImage from "../assets/aboutHero.jpeg";
import missionImage from "../assets/mission.jpg";
import visionImage from "../assets/vision.jpg";
import strategyImage from "../assets/tech-path-promo.png";
import "./CEO";

import {
  FaLaptopCode,
  FaHandshake,
  FaUsers,
  FaAward,
} from "react-icons/fa";

const cards = [
  {
    label: "OUR MISSION",
    image: missionImage,
    text: "To deliver innovative technology solutions that create measurable business value while helping organizations grow, transform, and succeed.",
  },
  {
    label: "OUR VISION",
    image: visionImage,
    text: "To become a trusted technology partner recognized for excellence, innovation, and the ability to turn complex business challenges into scalable digital solutions.",
  },
  {
    label: "OUR STRATEGY",
    image: strategyImage,
    text: "We combine technology expertise, experienced teams, and continuous innovation to create solutions that support our clients' evolving business needs.",
  },
];

const values = [
  {
    icon: Lightbulb,
    title: "Innovation",
    text: "We explore emerging tools and thoughtful ideas, then choose what solves the problem and creates lasting value.",
  },
  {
    icon: Sparkles,
    title: "Excellence",
    text: "We hold each project to clear standards for quality, performance, accessibility, and dependable delivery.",
  },
  {
    icon: Users,
    title: "Collaboration",
    text: "Our developers, designers, and clients share ideas early and work as one team from planning through launch.",
  },
  {
    icon: ShieldCheck,
    title: "Integrity",
    text: "We communicate openly, protect trust, and make responsible decisions at every stage of the work.",
  },
  {
    icon: Target,
    title: "Customer Success",
    text: "We begin with your goals and measure progress by the practical outcomes your business and users achieve.",
  },
  {
    icon: Layers,
    title: "Continuous Learning",
    text: "We reflect on results, share knowledge, and adapt our skills so each solution keeps pace with change.",
  },
];


const journey = [
  {
    year: "2024",
    text: "Expanded services into web development, software engineering, and UI/UX design.",
  },
  {
    year: "2025",
    text: "Built strong partnerships and delivered successful projects across industries.",
  },
  {
    year: "2026",
    text: "Continuing to innovate and helping businesses build the future with technology.",
  },
];


const faqs = [
  {
    question: "What services does Techlynx provide?",
    answer:
      "Techlynx provides web development, software solutions, UI/UX design, cloud services, automation, AI and data solutions, and digital transformation services.",
  },
  {
    question: "Can you build a custom website or application?",
    answer:
      "Yes. We build custom websites and applications based on your business requirements, goals, users, and technical needs.",
  },
  {
    question: "Do you work with startups and small businesses?",
    answer:
      "Yes. We work with startups, small businesses, and established organizations to create practical and scalable technology solutions.",
  },
  {
    question: "Can you improve an existing website or application?",
    answer:
      "Yes. We can analyze an existing product, improve its design and performance, fix technical issues, and add new features.",
  },
  {
    question: "Do you provide AI and automation solutions?",
    answer:
      "Yes. We can help businesses identify repetitive processes and implement AI, automation, and data-driven solutions to improve efficiency.",
  },
  {
    question: "How do we start a project with Techlynx?",
    answer:
      "Simply contact us and tell us about your idea, problem, or business requirement. We will understand your needs and discuss the next steps.",
  },
  {
    question: "How long does a project usually take?",
    answer:
      "Project timelines depend on the scope, features, complexity, and requirements. After understanding your project, we can provide a more specific timeline.",
  },
];



const stats = [
  {
    icon: <FaLaptopCode />,
    title: "PROJECTS DELIVERED",
    number: "20+",
    description:
      "Websites, platforms, and digital solutions built around real business needs.",
  },
  {
    icon: <FaHandshake />,
    title: "HAPPY CLIENTS",
    number: "25+",
    description:
      "Client partnerships shaped by trust, clear communication, and dependable delivery.",
  },
  {
    icon: <FaUsers />,
    title: "TEAM MEMBERS",
    number: "30+",
    description:
      "Designers, engineers, and strategists working together to solve complex challenges.",
  },
  {
    icon: <FaAward />,
    title: "YEARS OF EXPERIENCE",
    number: "+",
    description:
      "Years helping businesses adapt, improve, and grow through technology.",
  },
];

function Reveal({ children, className = "" }) {
  return (
    <div className={`reveal ${className}`}>
      {children}
    </div>
  );
}

/* ================= HERO ================= */

function AboutHero() {
  return (
    <section className="about-hero" style={{ minHeight: 0, height: "auto", alignItems: "flex-start", paddingTop: "42px" }}>
      <div className="hero-grid"></div>

      <div className="about-container" style={{ position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 0.85fr)", gap: "48px", alignItems: "start", padding: "0 0 40px" }}>

        <div className="breadcrumbs">
        
        </div>

        <div className="hero-copy">
          <Reveal>
            <p className="eyebrow light">
              <span></span>
              A technology partner for what’s next
            </p>

            <h1>
              We turn bold ideas into
              <br />
              <em>technology that moves you forward</em>
            </h1>
          </Reveal>

          <Reveal className="delay-1">
            <p className="hero-lede">
              Techlynx brings people, design, and engineering together to solve
              real business challenges. We build thoughtful digital experiences
              and dependable technology that help organizations take their next
              step with confidence.
            </p>

            <p className="hero-lede">
              From early discovery and product design through software
              development, AI integration, and cloud deployment, our teams
              connect every step. We work closely with each client to create
              practical solutions that can grow with their needs.
            </p>

            <p className="hero-lede">
              Our approach combines strategic thinking with hands-on technology expertise. We take the time to understand how a business operates, identify opportunities for improvement, and translate those opportunities into simple, scalable, and effective digital solutions.
            </p>

            <a href="#who-we-are" className="about-button">
              Get to know Techlynx
              <ArrowRight size={17} />
            </a>
          </Reveal>
        </div>

        <div className="about-hero-image-wrap">
          <img
            className="about-hero-image"
            src={aboutHeroImage}
            alt="Techlynx team building technology for the future"
          />
        </div>

      </div>

    </section>
  );
}

/* ================= WHO WE ARE ================= */

function WhoWeAre() {
  return (
    <section className="about-section who-section" id="who-we-are">

      <div className="about-container split-layout">

        <Reveal className="image-wrap">

          <img
            src={team}
            alt="Technology team collaborating"
          />

          <div className="image-caption">
            <span>01</span>

            <span>
              People behind
              <br />
              the possibilities
            </span>
          </div>

          <div className="image-corner"></div>

        </Reveal>

        <Reveal className="who-copy delay-1">

          <p className="eyebrow">
            <span></span>
            Our Perspective
          </p>

          <h2>
            Technology that connects ideas{" "}
            <em>with possibilities</em>
          </h2>

          <p>
            Techlynx is a team of passionate developers, designers,
            strategists, and technology experts committed to creating
            impactful digital experiences. We combine creativity, technology,
            and business understanding to deliver solutions that solve
            real-world challenges.
          </p>

          <p>
            From startups with ambitious ideas to established organizations
            undergoing digital transformation, we work closely with our
            clients to build reliable, scalable, and future-ready technology
            solutions.
          </p>

          <p>
            We listen first, then bring the right expertise together around
            each challenge. Clear communication and close collaboration help
            us create technology that is useful, dependable, and ready to grow
            with the people who use it.
          </p>

          <Link to="/services" className="text-link">
            Explore our services
            <ArrowRight size={17} />
          </Link>

        </Reveal>

      </div>

    </section>
  );
}
/* ================= MISSION & VISION ================= */
function PurposeSection() {
  return (
    <section className="purpose-section">

      {/* Background */}
      <div className="purpose-overlay"></div>

      {/* Heading */}
      <div className="purpose-heading">

        <span className="purpose-kicker">
          STRATEGIC DIRECTION
        </span>

        <h2>
          Purpose That Moves Business Forward
        </h2>

        <p>
          Our mission, vision, and strategy guide how we solve business
          challenges, build lasting partnerships, and deliver technology with
          measurable impact.
        </p>

      </div>

      {/* Cards */}
      <div className="purpose-cards">

        {cards.map((card, index) => (
          <div className="purpose-card" key={index}>

            {/* Image Area */}
            <div
              className="card-image"
              style={{
                backgroundImage: `url(${card.image})`,
              }}
            >

              <div className="image-dark-overlay"></div>

            </div>

            {/* Content Area */}
            <div className="card-content">

              <h3>{card.label}</h3>

              <p>{card.text}</p>

            </div>

          </div>
        ))}

      </div>

    </section>
  );
}
// =========== CoreValues=======================
function CoreValues() {
  return (
    <section className="about-section values-section">

      <div className="about-container">

        <Reveal className="section-intro values-intro">

          <p className="eyebrow1">
            <span></span>
            The way we work
          </p>

          <h2>
            The values that <em>drive us</em>
          </h2>

          <p>
            Our principles shape every decision, every product, and every
            relationship we build.
          </p>

        </Reveal>

        <div className="value-grid">

          {values.map(({ icon: Icon, title, text }, index) => (

            <Reveal className="value-card" key={title}>

              <div className="value-card-heading">
                <div className="value-top">
                  <Icon size={23} aria-hidden="true" />
                </div>
                <h3>{title}</h3>
              </div>

              <p>{text}</p>

            </Reveal>

          ))}

        </div>

      </div>

    </section>
  );
}

// ===================FAQ ====================
function FAQ() {
  const [active, setActive] = useState(0);

   return (
    <section className="faq-section" id="faq">
      <div className="faq-container">

        <div className="faq-header">
          <div>
            <div className="faq-label">
              <span></span>
              FAQ
            </div>

            <h2>
              Know More{" "}
              <span>About Us</span>
            </h2>
          </div>

          <p>
            Everything you need to know about our services,
            projects, technology, and the way we work.
          </p>
        </div>

        <div className="faq-list">

          {faqs.map((faq, index) => (
            <div
              className={`faq-item ${
                active === index ? "active" : ""
              }`}
              key={index}
            >

              {/* QUESTION */}
              <button
                className="faq-question"
                onClick={() =>
                  setActive(active === index ? null : index)
                }
              >
                <span className="faq-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span className="faq-question-text">
                  {faq.question}
                </span>

                <span className="faq-icon">
                  {active === index ? "−" : "+"}
                </span>
              </button>

              {/* ANSWER */}
              {active === index && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}

            </div>
          ))}

        </div>

      </div>
    </section>
  );
}


// ==========Stats============
const Stats = () => {
  return (
    <section className="stats-section">
      <div className="stats-container">
        {stats.map((stat, index) => (
          <div className="stat-card" key={index}>
            <div className="stat-header">
              <div className="stat-icon">{stat.icon}</div>

              <h3>{stat.title}</h3>
            </div>

            <div className="stat-number">{stat.number}</div>

            <p>{stat.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ================= JOURNEY ================= */

function Journey() {
const navigate = useNavigate();
  return (
    <section className="about-section journey-section">

      <div className="about-container">

        <Reveal className="section-intro journey-intro">

          <p className="eyebrow light">
            <span></span>
            The path so far
          </p>

          <h2 id="white">
            Our <em>Journey</em>
          </h2>

          <p>
            A young company with a long-term mindset. We started with a simple
            belief: technology should make progress feel possible.
          </p>

        </Reveal>

        <div className="timeline">

          {journey.map((item, index) => (

            <Reveal
              key={item.year}
              className={`timeline-item ${
                index % 2 ? "timeline-right" : ""
              }`}
            >

              <div className="timeline-year">
                {item.year}
                <span></span>
              </div>

              <div className="timeline-card">

                <span className="timeline-index">
                  0{index + 1}
                </span>

                <p>{item.text}</p>

              </div>

            </Reveal>

          ))}
          <button className="CEO-button" onClick={() => navigate("/CEO")}>
            CEO Journey
          </button>
        </div>

      </div>

    </section>
  );
}
          

/* ================= ABOUT PAGE ================= */

function About() {

  useEffect(() => {

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }

        });

      },
      {
        threshold: 0.12,
      }
    );

    const elements =
      document.querySelectorAll(".about-page .reveal");

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };

  }, []);

  return (
    <main className="about-page">

      <AboutHero />

      <WhoWeAre />

      <PurposeSection />

      <CoreValues />

      <Journey />
      
      <FAQ />

      <Stats />

    </main>
  );
}

export default About;
